/**
 * O assistente de IA: decide se aparece, guarda a conversa e fala com a API.
 *
 * Montado pelo `AppShell` com `key={idCliente}` — trocar de cliente REMONTA este
 * componente, o que zera a conversa e aborta a espera em andamento. É assim que a
 * conversa nunca atravessa tenants (épico AC-3), sem depender de alguém lembrar de
 * limpar estado.
 *
 * 🔒 BR-25 — `enviarMensagem` só é chamado de `enviar()`, que só roda num envio ou
 * num clique de chip. Os dois efeitos abaixo são de limpeza e de foco; nenhum toca
 * a API.
 */
import { useEffect, useReducer, useRef, useState } from 'react'

import { useFlag } from '@/flags/flags'

import { BotaoDoAssistente, type Novidade } from './BotaoDoAssistente'
import { CONVERSA_VAZIA, reduzirConversa } from './conversa'
import { enviarMensagem } from './enviarMensagem'
import { avisoDoErro, FalhaDoAssistente } from './falhas'
import { PainelDoAssistente } from './PainelDoAssistente'
import { ESPECIALIDADES, SUGESTOES, TEXTOS } from './textos'
import { useAssistenteVisivel } from './visibilidade'

export function Assistente({ idCliente }: { idCliente: string }) {
  const visivel = useAssistenteVisivel()
  const finops = useFlag('modulo.finops')
  const orcamento = useFlag('modulo.orcamento')
  const compliance = useFlag('modulo.compliance')

  const [conversa, despachar] = useReducer(reduzirConversa, CONVERSA_VAZIA)
  const [aberto, setAberto] = useState(false)
  const [jaAbriu, setJaAbriu] = useState(false)
  /** A pergunta que está sendo digitada. Aqui, e não no painel, para sobreviver a um minimizar. */
  const [rascunho, setRascunho] = useState('')
  /**
   * O que chegou e ainda não foi visto. Toda chegada marca; abrir E minimizar
   * limpam — então só sobra marcado o que chegou DEPOIS de o painel ser recolhido.
   */
  const [novidade, setNovidade] = useState<Novidade>(null)
  /** O envio em andamento. `null` quando não há nenhum. */
  const envio = useRef<AbortController | null>(null)
  const botao = useRef<HTMLButtonElement>(null)

  // Desmontar (trocar de cliente, sair do escopo, logout) interrompe a espera.
  useEffect(() => () => envio.current?.abort(), [])

  // Ao recolher, o foco volta para o botão que abriu o painel.
  useEffect(() => {
    if (!aberto && jaAbriu) botao.current?.focus()
  }, [aberto, jaAbriu])

  function enviar(texto: string) {
    // `envio.current`, e não `conversa.pendente`: dois Enter no mesmo tique leem o
    // mesmo render, e só a ref já mudou entre um e outro.
    if (envio.current) return

    const controlador = new AbortController()
    envio.current = controlador
    despachar({ tipo: 'enviou', id: crypto.randomUUID(), texto })

    enviarMensagem(
      { idCliente, idConversa: conversa.idConversa, mensagem: texto },
      { signal: controlador.signal },
    )
      .then((resposta) => {
        // Quem fechou o painel não quer esta resposta na conversa nova.
        if (controlador.signal.aborted) return
        envio.current = null
        setNovidade('resposta')
        despachar({
          tipo: 'respondeu',
          id: crypto.randomUUID(),
          idConversa: resposta.idConversa,
          texto: resposta.texto,
          especialistas: resposta.especialistasAcionados,
        })
      })
      .catch((erro: unknown) => {
        if (controlador.signal.aborted) return
        envio.current = null
        setNovidade('aviso')
        despachar({
          tipo: 'falhou',
          id: crypto.randomUUID(),
          idConversa: erro instanceof FalhaDoAssistente ? erro.idConversa : null,
          ...avisoDoErro(erro),
        })
      })
  }

  function abrir() {
    setNovidade(null)
    setAberto(true)
    setJaAbriu(true)
  }

  /** Recolhe e MANTÉM a conversa. O que está na tela neste momento já foi visto. */
  function minimizar() {
    setNovidade(null)
    setAberto(false)
  }

  /** Recolhe E encerra: a próxima pergunta abre uma conversa nova. */
  function fechar() {
    envio.current?.abort()
    envio.current = null
    despachar({ tipo: 'encerrou' })
    setRascunho('')
    setNovidade(null)
    setAberto(false)
  }

  if (!visivel) return null

  if (!aberto) {
    return (
      <>
        <BotaoDoAssistente ref={botao} flutuando={!jaAbriu} novidade={novidade} aoAbrir={abrir} />
        {/* Sempre montado ao lado do botão, mesmo vazio: uma região viva só anuncia
            o que MUDA depois de ela existir. `aria-live`, e NÃO `role="status"`: este
            componente está em toda tela de cliente, e um `status` vazio a mais seria
            confundido com o aviso da própria tela. */}
        <span aria-live="polite" className="sr-only">
          {novidade === 'resposta' && TEXTOS.novaResposta}
          {novidade === 'aviso' && TEXTOS.novoAviso}
        </span>
      </>
    )
  }

  // Épico AC-2: o painel não oferece ajuda de um módulo que o cliente não tem.
  const modulosLigados: Record<string, boolean> = {
    'modulo.finops': finops,
    'modulo.orcamento': orcamento,
    'modulo.compliance': compliance,
  }

  return (
    <PainelDoAssistente
      conversa={conversa}
      especialidades={ESPECIALIDADES.filter((e) => modulosLigados[e.flag]).map((e) => e.rotulo)}
      sugestoes={SUGESTOES.filter((s) => modulosLigados[s.flag]).map((s) => s.texto)}
      rascunho={rascunho}
      aoMudarRascunho={setRascunho}
      aoEnviar={enviar}
      // Minimizar NÃO aborta: a resposta chega, marca o botão e espera o painel reabrir.
      aoMinimizar={minimizar}
      aoFechar={fechar}
    />
  )
}
